import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import StatStripSkeleton from '@/components/ui/StatStripSkeleton'
import { cn } from '@/lib/utils'

/**
 * The loading shapes of the Analytics page.
 *
 * Its own light module, importing the kit's skeletons only: the route's
 * `pendingComponent` ships in the entry chunk, so it may not pull a component
 * module in (docs/route-loading.md, Skeletons).
 */

/** `AnalyticsStatStrip`'s three cells, in the panel the loaded strip sits in, at its 24 figures. */
export function AnalyticsStatsSkeleton() {
  return <StatStripSkeleton cells={3} surface="panel" size="hero" />
}

/**
 * `TabBar`'s underline row: 36 tall, 20 between four labels (the widths are the labels at 16px
 * 600), and the period caption's bar at the right from `@md`, where the loaded caption shows.
 */
const TAB_WIDTHS = ['w-32', 'w-28', 'w-24', 'w-28'] as const

export function AnalyticsTabsSkeleton() {
  return (
    <div className="flex h-9 items-center justify-between gap-3 overflow-hidden">
      <div className="flex items-center gap-5">
        {TAB_WIDTHS.map((width) => (
          <Skeleton key={width} className={cn('h-5 shrink-0', width)} />
        ))}
      </div>
      <Skeleton className="hidden h-3 w-28 shrink-0 rounded-xs @md:block" />
    </div>
  )
}

/**
 * How many rows a tab's table skeleton holds. The stock-count tab draws
 * exactly this many; Most popular (20) and Sector diversity (56) draw more,
 * so their skeletons undersell them — harmless, since each is the last block
 * on the page and growing downwards moves nothing.
 */
export const TAB_TABLE_ROWS = 10

/** The Fund stock count tab: its 10-row table (Fund, Stocks held, Portfolio value). */
export function StockCountTabSkeleton() {
  return <DataTableSkeleton rows={TAB_TABLE_ROWS} columns={3} rowHeight="lg" />
}
